import { useEffect, useState } from "react";
import {
  useGetInvoicesQuery,
  useGetCustomersQuery,
  useGetPaymentQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
} from "../../../../redux/api";
import type { Payment as PaymentModel } from "../../../../redux/api";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";

interface PaymentFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type PaymentForm = {
  paymentNumber: string;
  invoiceId: string;
  customerId: string;
  paymentDate: string;
  amount: string;
  method: string;
  reference: string;
  status: string;
  notes: string;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm: PaymentForm = {
  paymentNumber: "",
  invoiceId: "",
  customerId: "",
  paymentDate: todayISO(),
  amount: "",
  method: "cash",
  reference: "",
  status: "completed",
  notes: "",
};

const formFromPayment = (existing: PaymentModel): PaymentForm => ({
  paymentNumber: existing.paymentNumber ?? "",
  invoiceId: existing.invoiceId ?? "",
  customerId: existing.customerId ?? "",
  paymentDate: (existing.paymentDate ?? todayISO()).slice(0, 10),
  amount: existing.amount != null ? String(existing.amount) : "",
  method: existing.method ?? "cash",
  reference: existing.reference ?? "",
  status: existing.status ?? "completed",
  notes: existing.notes ?? "",
});

const n = (v: string) => Number(v) || 0;

export const PaymentFormModal = ({ open, id, onClose }: PaymentFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetPaymentQuery(activeId ?? "", {
    skip: !activeId,
  });

  const { data: invoices = [] } = useGetInvoicesQuery();
  const { data: customers = [] } = useGetCustomersQuery();

  const [createPayment, { isLoading: creating }] = useCreatePaymentMutation();
  const [updatePayment, { isLoading: updating }] = useUpdatePaymentMutation();

  const [form, setForm] = useState<PaymentForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromPayment(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (!existing) return;
    setForm(formFromPayment(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof PaymentForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (n(form.amount) <= 0) errors.amount = "Amount must be greater than zero.";
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      paymentNumber: form.paymentNumber.trim() || undefined,
      invoiceId: form.invoiceId || undefined,
      customerId: form.customerId || undefined,
      paymentDate: form.paymentDate,
      amount: n(form.amount),
      method: form.method,
      reference: form.reference.trim() || undefined,
      status: form.status,
      notes: form.notes.trim() || undefined,
    };

    try {
      if (isEdit && activeId) {
        await updatePayment({ id: activeId, input: payload }).unwrap();
      } else {
        await createPayment({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save payment.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit payment" : "New payment"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create payment"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <label className="field">
          Invoice
          <select
            value={form.invoiceId}
            onChange={(e) => setField("invoiceId", e.target.value)}
          >
            <option value="">— None —</option>
            {invoices.map((i) => (
              <option key={i.id} value={i.id}>
                {i.invoiceNumber}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Customer
          <select
            value={form.customerId}
            onChange={(e) => setField("customerId", e.target.value)}
          >
            <option value="">— None —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Payment date
          <input
            type="date"
            value={form.paymentDate}
            onChange={(e) => setField("paymentDate", e.target.value)}
          />
        </label>
        <Field label="Amount *" error={fieldErrors.amount}>
          <input
            type="number"
            value={form.amount}
            onChange={(e) => {
              setField("amount", e.target.value);
              setFieldErrors((prev) => (prev.amount ? { ...prev, amount: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          Method
          <select
            value={form.method}
            onChange={(e) => setField("method", e.target.value)}
          >
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="bank">Bank transfer</option>
            <option value="upi">UPI</option>
            <option value="cheque">Cheque</option>
          </select>
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </label>
        <label className="field">
          Payment number
          <input
            value={form.paymentNumber}
            onChange={(e) => setField("paymentNumber", e.target.value)}
            placeholder="PMT-001"
          />
        </label>
        <label className="field">
          Reference
          <input
            value={form.reference}
            onChange={(e) => setField("reference", e.target.value)}
            placeholder="Txn / cheque no."
          />
        </label>
        <label className="field field--full">
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
};
